import { times } from 'lodash/fp'
import { ElementRef } from 'react'

import {
  Box,
  BoxProps,
  Divider,
  Flex,
  HStack,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderProps,
  SliderThumb,
  SliderTrack,
  useControllableState,
} from '@masterplandev/ui'

function SliderLabel(props: BoxProps) {
  return (
    <Box
      lineHeight="shorter"
      textStyle="subhead"
      fontSize="xx-small"
      color="mpWhite.alpha40"
      w="32"
      {...props}
    />
  )
}

type PollSliderProps = SliderProps & {
  minLabel: string
  maxLabel: string
}

export function PollSlider({
  minLabel,
  maxLabel,
  onChange,
  ...sliderProps
}: PollSliderProps) {
  const [value, setSliderValue] = useControllableState({ onChange })

  const handleClick = (event: React.MouseEvent<ElementRef<'div'>>) => {
    const clickedOnLeftHalf = event.clientX < document.body.clientWidth / 2
    const currentValue = value ?? sliderProps.defaultValue
    const newValue = clickedOnLeftHalf
      ? Math.max(sliderProps.min || 0, currentValue - 1)
      : Math.min(sliderProps.max || 0, currentValue + 1)

    setSliderValue(newValue)
  }

  const getFilledTrackGradient = () => {
    const stepToGradient = {
      1: 'linear(to-r, #E78D64, #D5A46C)',
      2: 'linear(to-r,  #E78D64, #D5A46C)',
      3: 'linear(to-r, #E78D64, #C2BA72)',
      4: 'linear(to-r, #E78D64, #D5B766, #7ED0A5)',
      5: 'linear(to-r,  #E78D64, #D5B766, #7ED0A5, #4FCCE8)',
    }
    return value in stepToGradient ? stepToGradient[value] : stepToGradient[5]
  }

  const getMarkerColor = () => {
    const stepToMarkerColor = {
      1: '#D56C6C',
      2: '#D5A46C',
      3: '#C2BA72',
      4: '#76C1AB',
      5: '#4FCCE8',
    }
    return stepToMarkerColor[value]
  }

  return (
    <>
      <Box position="absolute" inset="0" onClick={handleClick} />
      <HStack spacing="12">
        <SliderLabel textAlign="right">{minLabel}</SliderLabel>
        <Box width="full" onClick={(event) => event.stopPropagation()}>
          <Slider
            {...sliderProps}
            value={value}
            step={1}
            onChange={(val) => {
              setSliderValue(val)
            }}
            onChangeEnd={(val) => {
              setSliderValue(val)
            }}
            aria-label="slider">
            <SliderTrack bgColor="mpWhite.alpha8" height="2.5">
              <SliderFilledTrack bgGradient={getFilledTrackGradient()} />
            </SliderTrack>
            <SliderThumb boxSize="5" bgColor="mpWhite.base" />
            <Flex>
              {times(
                (index) => (
                  <SliderMark key={index} bottom="-3" value={index + 1}>
                    <Divider
                      orientation="vertical"
                      height="1"
                      borderWidth={index + 1 === value ? 'thin' : '0.5px'}
                      borderColor={
                        index + 1 === value
                          ? getMarkerColor()
                          : 'mpWhite.alpha16'
                      }
                    />
                  </SliderMark>
                ),
                sliderProps.max || 5,
              )}
            </Flex>
          </Slider>
        </Box>
        <SliderLabel>{maxLabel}</SliderLabel>
      </HStack>
    </>
  )
}
